import { useState, useEffect, useRef, useCallback } from 'react';
import config from '../../../utils/config';
import { useAuth } from '../../context/wms-context';

const collapseRepeatedScanPayload = (rawScanValue) => {
    const trimmedScanValue = (rawScanValue || '').trim();
    const payloadLength = trimmedScanValue.length;
    if (!payloadLength || payloadLength % 2 !== 0) {
        return trimmedScanValue;
    }

    const halfPayloadLength = payloadLength / 2;
    const firstHalfPayload = trimmedScanValue.slice(0, halfPayloadLength);
    const secondHalfPayload = trimmedScanValue.slice(halfPayloadLength);
    return firstHalfPayload === secondHalfPayload ? firstHalfPayload : trimmedScanValue;
};

const SCANNER_DEBUG_LOG_LIMIT = 12;

export default function ScanPutawayLines({
    selectedLines,
    setProcessBatch,
    setSelectedLines,
    handleSetNotification,
    setScannerStep,
    setActiveLineForLocation,
    scannerSteps,
    normalizeInventoryLineName,
    showScannerDebug = false,
}) {
    const ORIGIN = config.origin;
    const { currentUser } = useAuth();
    const selectedLinesRef = useRef(selectedLines);
    const hiddenScannerInputRef = useRef(null);
    const hiddenInputBufferRef = useRef('');
    const scannerInputReadOnlyReleaseTimeoutRef = useRef(null);
    const lastSubmissionRef = useRef({ payload: '', submittedAtMs: 0 });
    const [lastCapturedScanValue, setLastCapturedScanValue] = useState('');
    const [scanBufferPreviewValue, setScanBufferPreviewValue] = useState('');
    const [lastSubmittedScanPayload, setLastSubmittedScanPayload] = useState('');
    const [lastApiDebugStatus, setLastApiDebugStatus] = useState('No API request yet');
    const [scannerDebugLogEntries, setScannerDebugLogEntries] = useState([]);
    const [hiddenInputFocused, setHiddenInputFocused] = useState(false);
    const [loadingScannedLine, setLoadingScannedLine] = useState(false);
    const [creatingBatch, setCreatingBatch] = useState(false);
    const [inputError, setInputError] = useState(false);
    const [scannerInputReadOnly, setScannerInputReadOnly] = useState(true);

    const appendScannerDebugEntry = useCallback(
        (debugLabel, debugMessage) => {
            if (!showScannerDebug) {
                return;
            }
            const timestampLabel = new Date().toLocaleTimeString();
            const nextDebugEntry = `${timestampLabel} | ${debugLabel}: ${debugMessage}`;
            setScannerDebugLogEntries((previousScannerDebugLogEntries) => {
                const updatedScannerDebugLogEntries = [...previousScannerDebugLogEntries, nextDebugEntry];
                return updatedScannerDebugLogEntries.slice(-SCANNER_DEBUG_LOG_LIMIT);
            });
        },
        [showScannerDebug]
    );

    const focusHiddenScannerInput = useCallback(() => {
        if (loadingScannedLine || creatingBatch) {
            return;
        }
        const scannerInputElement = hiddenScannerInputRef.current;
        if (!scannerInputElement) {
            return;
        }

        if (scannerInputReadOnlyReleaseTimeoutRef.current) {
            clearTimeout(scannerInputReadOnlyReleaseTimeoutRef.current);
            scannerInputReadOnlyReleaseTimeoutRef.current = null;
        }

        setScannerInputReadOnly(true);

        const focusScannerField = () => {
            try {
                scannerInputElement.focus({ preventScroll: true });
            } catch (focusError) {
                scannerInputElement.focus();
            }
            scannerInputReadOnlyReleaseTimeoutRef.current = window.setTimeout(() => {
                setScannerInputReadOnly(false);
            }, 120);
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(focusScannerField);
            return;
        }

        focusScannerField();
    }, [loadingScannedLine, creatingBatch]);

    const clearHiddenInputValue = useCallback(() => {
        hiddenInputBufferRef.current = '';
        if (hiddenScannerInputRef.current) {
            hiddenScannerInputRef.current.value = '';
        }
        setScanBufferPreviewValue('');
    }, []);

    const shouldSkipDuplicateSubmission = useCallback((nextSubmissionPayload) => {
        const dedupeWindowMs = 250;
        const nowMs = Date.now();
        const { payload: previousPayload, submittedAtMs: previousSubmittedAtMs } = lastSubmissionRef.current;
        if (
            previousPayload &&
            previousPayload === nextSubmissionPayload &&
            nowMs - previousSubmittedAtMs <= dedupeWindowMs
        ) {
            return true;
        }

        lastSubmissionRef.current = {
            payload: nextSubmissionPayload,
            submittedAtMs: nowMs,
        };
        return false;
    }, []);

    const handleScannedInventoryLine = async (rawScannedInventoryLineName) => {
        appendScannerDebugEntry('scanRaw', rawScannedInventoryLineName || '(empty)');
        const normalizedInventoryLineName = normalizeInventoryLineName(rawScannedInventoryLineName);
        appendScannerDebugEntry('scanNormalized', normalizedInventoryLineName || '(empty)');
        if (!normalizedInventoryLineName) {
            appendScannerDebugEntry('scanIgnored', 'normalized inventory line was empty');
            return;
        }

        // Clear any previous notification immediately when a new scan is processed.
        handleSetNotification(null);

        if (
            selectedLinesRef.current.find(
                (selectedLine) => normalizeInventoryLineName(selectedLine.Name) === normalizedInventoryLineName
            )
        ) {
            setInputError(false);
            setLastApiDebugStatus('INV already in selected list (request skipped)');
            appendScannerDebugEntry('scanDuplicate', normalizedInventoryLineName);
            handleSetNotification({
                title: 'Inventory Line Already Selected',
                desc: `Inventory line ${normalizedInventoryLineName} is already in this batch.`,
                status: 'question',
            });
            return;
        }

        setLoadingScannedLine(true);
        setInputError(false);
        setLastApiDebugStatus(`Requesting inventory line data for ${normalizedInventoryLineName}`);
        appendScannerDebugEntry('fetchStart', '/wms/putaway/get-inventory-line-data');

        try {
            const inventoryLineResponse = await fetch(`${ORIGIN}/wms/putaway/get-inventory-line-data`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    inventoryLineName: normalizedInventoryLineName,
                    authentication: {
                        userId: currentUser.authentication.userId,
                        userEmail: currentUser.authentication.userEmail,
                        userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
                    },
                }),
            });

            const inventoryLineData = await inventoryLineResponse.json();
            if (inventoryLineData.error || !inventoryLineData[0]) {
                setInputError(true);
                setLastApiDebugStatus(`Inventory lookup failed for ${normalizedInventoryLineName}`);
                appendScannerDebugEntry(
                    'fetchError',
                    inventoryLineData?.message || `No line returned for ${normalizedInventoryLineName}`
                );
                handleSetNotification({
                    title: 'Inventory Line Not Found',
                    desc: `Unable to load ${normalizedInventoryLineName}. Scan again.`,
                    status: 'error',
                });
                return;
            }

            setInputError(false);
            setLastApiDebugStatus(`Inventory lookup success for ${normalizedInventoryLineName}`);
            appendScannerDebugEntry('fetchSuccess', `Loaded ${normalizedInventoryLineName}`);
            handleSetNotification(null);
            setSelectedLines((previousSelectedLines) => {
                const isLineAlreadySelected = previousSelectedLines.find(
                    (selectedLine) => selectedLine.Id === inventoryLineData[0].Id
                );
                if (isLineAlreadySelected) {
                    return previousSelectedLines;
                }
                return [...previousSelectedLines, inventoryLineData[0]];
            });
        } catch (error) {
            setInputError(true);
            setLastApiDebugStatus(`Inventory lookup request failed for ${normalizedInventoryLineName}`);
            appendScannerDebugEntry('fetchError', error?.message || 'inventory lookup request failed');
            handleSetNotification({
                title: 'Inventory Line Scan Failed',
                desc: 'Unable to process this scan right now. Please scan again.',
                status: 'error',
            });
        } finally {
            setLoadingScannedLine(false);
        }
    };

    const submitScannedPayload = async (providedScannedValue, captureSource = 'globalKeyListener') => {
        const rawScannedValue = providedScannedValue || '';
        const scannedValue = collapseRepeatedScanPayload(rawScannedValue);
        setLastCapturedScanValue(scannedValue);
        setLastSubmittedScanPayload(scannedValue || '(empty)');
        setScanBufferPreviewValue('');
        appendScannerDebugEntry('submitRaw', rawScannedValue || '(empty)');
        appendScannerDebugEntry('submitCollapsed', scannedValue || '(empty)');
        appendScannerDebugEntry('submitSource', captureSource);

        if (!scannedValue) {
            appendScannerDebugEntry('submitIgnored', 'collapsed payload was empty');
            return;
        }

        if (shouldSkipDuplicateSubmission(scannedValue)) {
            appendScannerDebugEntry('submitDeduped', `${captureSource} duplicate ignored`);
            return;
        }

        await handleScannedInventoryLine(scannedValue);
    };

    const syncHiddenInputBufferValue = useCallback(
        (nextInputValue, debugLabel = 'hiddenInputBuffer') => {
            const inputValue = nextInputValue || '';
            hiddenInputBufferRef.current = inputValue;
            setScanBufferPreviewValue(inputValue);
            setLastCapturedScanValue(inputValue || '');
            appendScannerDebugEntry(debugLabel, `${inputValue || '(empty)'} | len=${inputValue.length}`);
        },
        [appendScannerDebugEntry]
    );

    const resolveDeferredHiddenInputSubmit = useCallback(async () => {
        const submittedValue = (hiddenInputBufferRef.current || hiddenScannerInputRef.current?.value || '').trim();
        appendScannerDebugEntry(
            'hiddenInputSubmitResolved',
            `${submittedValue || '(empty)'} | len=${submittedValue.length}`
        );
        clearHiddenInputValue();
        await submitScannedPayload(submittedValue, 'hiddenInput');
    }, [appendScannerDebugEntry, clearHiddenInputValue]);

    const handleHiddenScannerInputChange = (inputChangeEvent) => {
        const inputValue = inputChangeEvent.target.value || '';
        syncHiddenInputBufferValue(inputValue, 'hiddenInputBuffer');
    };

    const handleHiddenScannerInputKeyDown = async (keyboardEvent) => {
        if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== 'NumpadEnter') {
            return;
        }

        appendScannerDebugEntry(
            'hiddenInputEnter',
            `${keyboardEvent.key || '(empty)'} | len=${(keyboardEvent.currentTarget?.value || '').length}`
        );
        keyboardEvent.preventDefault();
        appendScannerDebugEntry('hiddenInputSubmitDeferred', keyboardEvent.key);
        await resolveDeferredHiddenInputSubmit();
    };

    const handleRemoveSelectedLine = (selectedLineId) => {
        setSelectedLines((previousSelectedLines) =>
            previousSelectedLines.filter((selectedLine) => selectedLine.Id !== selectedLineId)
        );
    };

    const handleProcessBatch = async () => {
        if (selectedLinesRef.current.length === 0) {
            appendScannerDebugEntry('processBatchBlocked', 'no selected lines');
            handleSetNotification({
                title: 'No Lines Selected',
                desc: 'Scan at least one inventory line to create a batch.',
                status: 'error',
            });
            return;
        }

        setCreatingBatch(true);
        setLastApiDebugStatus('Submitting selected lines to create putaway batch');
        appendScannerDebugEntry('fetchStart', '/wms/putaway/set-putaway-batch');
        try {
            await fetch(`${ORIGIN}/wms/putaway/set-putaway-batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    selectedLines: selectedLinesRef.current,
                    authentication: {
                        userId: currentUser.authentication.userId,
                        userEmail: currentUser.authentication.userEmail,
                        userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
                    },
                }),
            });
            setLastApiDebugStatus('Batch creation request completed');
            appendScannerDebugEntry('fetchSuccess', '/wms/putaway/set-putaway-batch');
            setProcessBatch(true);
            setScannerStep(scannerSteps.awaitInventoryLineSelection);
            setActiveLineForLocation(null);
            appendScannerDebugEntry('stateTransition', scannerSteps.awaitInventoryLineSelection);
        } catch (error) {
            setLastApiDebugStatus('Batch creation request failed');
            appendScannerDebugEntry('fetchError', error?.message || '/wms/putaway/set-putaway-batch failed');
            handleSetNotification({
                title: 'Batch Creation Failed',
                desc: 'Unable to create the putaway batch right now. Please try again.',
                status: 'error',
            });
        } finally {
            setCreatingBatch(false);
        }
    };

    useEffect(() => {
        selectedLinesRef.current = selectedLines;
    }, [selectedLines]);

    useEffect(() => {
        return () => {
            if (scannerInputReadOnlyReleaseTimeoutRef.current) {
                clearTimeout(scannerInputReadOnlyReleaseTimeoutRef.current);
                scannerInputReadOnlyReleaseTimeoutRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        focusHiddenScannerInput();
    }, [focusHiddenScannerInput]);

    return (
        <div className='flex flex-col items-center px-0 w-full sm:px-2'>
            <input
                ref={hiddenScannerInputRef}
                type='text'
                autoComplete='off'
                autoCorrect='off'
                autoCapitalize='off'
                spellCheck={false}
                inputMode='text'
                showSoftInputOnFocus={false}
                readOnly={scannerInputReadOnly}
                className='fixed right-2 bottom-2 z-50 p-1 w-24 h-8 text-[10px] text-green-700 bg-green-100 rounded border border-green-500'
                style={{
                    position: 'fixed',
                    right: '8px',
                    bottom: '8px',
                    opacity: 0.85,
                }}
                placeholder='Scan field'
                onFocus={() => {
                    setHiddenInputFocused(true);
                    appendScannerDebugEntry('hiddenInputFocus', 'focused');
                }}
                onBlur={() => {
                    setHiddenInputFocused(false);
                    appendScannerDebugEntry('hiddenInputFocus', 'blurred');
                }}
                onChange={handleHiddenScannerInputChange}
                onKeyDown={handleHiddenScannerInputKeyDown}
            />
            <h3 className='mb-2 text-sm font-bold text-center sm:text-base'>Scanner Putaway: Scan INV lines</h3>

            <div className='w-full max-w-full'>
                <div className='flex flex-col gap-2 justify-center items-end mb-4 sm:flex-row sm:gap-x-2'>
                    <div
                        className={`flex flex-col p-2 w-full rounded-md border text-xs sm:text-sm sm:w-64 ${
                            inputError ? 'text-red-600 border-red-500' : 'text-gray-700 border-gray-300'
                        }`}
                    >
                        <p className='font-semibold'>Scanner Status</p>
                        <p>Ready to scan INV lines</p>
                        <p className='mt-1 break-all text-[11px] sm:text-xs'>
                            Last scan: {lastCapturedScanValue || 'Waiting for scan...'}
                        </p>
                    </div>
                    {showScannerDebug ? (
                        <div className='p-2 w-full text-xs text-green-700 rounded-md border border-green-500 bg-green-50 sm:w-[28rem] sm:text-sm'>
                            <p className='font-semibold text-green-700'>Scanner Debug</p>
                            <p>Current Step: {scannerSteps.collectBatchLines}</p>
                            <p>Capture Paused: {loadingScannedLine || creatingBatch ? 'true' : 'false'}</p>
                            <p>Hidden Input Focused: {hiddenInputFocused ? 'true' : 'false'}</p>
                            <p className='break-all'>Buffer Preview: {scanBufferPreviewValue || '(empty)'}</p>
                            <p className='break-all'>Last Submitted: {lastSubmittedScanPayload || '(none)'}</p>
                            <p className='break-all'>Last API Status: {lastApiDebugStatus}</p>
                            <div className='pt-1 mt-1 border-t border-green-300'>
                                {scannerDebugLogEntries.length === 0 ? (
                                    <p>No scanner events yet.</p>
                                ) : (
                                    scannerDebugLogEntries.map((scannerDebugLogEntry) => (
                                        <p key={scannerDebugLogEntry} className='leading-tight break-all'>
                                            {scannerDebugLogEntry}
                                        </p>
                                    ))
                                )}
                            </div>
                        </div>
                    ) : null}
                    <button
                        type='button'
                        className='p-2 w-full h-10 text-sm text-white rounded-md sm:h-12 bg-blue-primary sm:w-auto hover:bg-blue-primary/90 sm:text-base'
                        onClick={handleProcessBatch}
                        disabled={loadingScannedLine || creatingBatch}
                    >
                        {creatingBatch ? 'Creating Batch' : 'Process Batch'}
                    </button>
                </div>
            </div>

            <div className='w-full sm:max-w-6xl'>
                <table className='w-full text-xs table-auto sm:text-sm'>
                    <thead className='text-white bg-blue-primary'>
                        <tr className='flex'>
                            <th className='pl-1 sm:pl-4 flex-[1.2_1_0%] min-w-0 py-1 sm:py-2 text-left text-[10px] sm:text-sm'>
                                INV
                            </th>
                            <th className='pl-1 sm:pl-4 flex-[1.5_1_0%] min-w-0 py-1 sm:py-2 text-left text-[10px] sm:text-sm'>
                                Part Number
                            </th>
                            <th className='pl-1 sm:pl-4 flex-[0.6_1_0%] min-w-0 py-1 sm:py-2 text-left text-[10px] sm:text-sm'>
                                CC
                            </th>
                            <th className='pl-1 sm:pl-4 flex-[0.7_1_0%] min-w-0 py-1 sm:py-2 text-left text-[10px] sm:text-sm'>
                                Qty Avail
                            </th>
                            <th className='pl-1 sm:pl-4 flex-[1.2_1_0%] min-w-0 py-1 sm:py-2 text-left text-[10px] sm:text-sm'>
                                Current Location
                            </th>
                            <th className='pl-1 sm:pl-4 flex-[0.3_1_0%] min-w-0 py-1 sm:py-2 text-left text-[10px] sm:text-sm' />
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: Math.max(10, selectedLines.length) }).map((_, rowIndex) => {
                            const selectedLine = selectedLines[rowIndex];
                            return (
                                <tr
                                    key={selectedLine?.Id || `empty-${rowIndex}`}
                                    className='flex items-center border even:bg-gray-200 min-h-[36px] sm:h-10'
                                >
                                    <td className='pl-1 sm:pl-4 flex-[1.2_1_0%] min-w-0 py-0.5 sm:py-0'>
                                        {selectedLine ? (
                                            <a
                                                className='text-[10px] text-blue-900 underline break-words cursor-pointer hover:text-blue-400 sm:text-sm truncate block'
                                                href={selectedLine.invLineLink}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                title={selectedLine?.Name || ''}
                                            >
                                                {selectedLine?.Name || ''}
                                            </a>
                                        ) : (
                                            <span>{''}</span>
                                        )}
                                    </td>
                                    <td
                                        className='pl-1 sm:pl-4 flex-[1.5_1_0%] min-w-0 py-0.5 sm:py-0 text-[10px] sm:text-sm break-words truncate'
                                        title={selectedLine?.productName || ''}
                                    >
                                        {selectedLine?.productName || ''}
                                    </td>
                                    <td className='pl-1 sm:pl-4 flex-[0.6_1_0%] min-w-0 py-0.5 sm:py-0 text-[10px] sm:text-sm'>
                                        {selectedLine?.conditionCode || ''}
                                    </td>
                                    <td className='pl-1 sm:pl-4 flex-[0.7_1_0%] min-w-0 py-0.5 sm:py-0 text-[10px] sm:text-sm'>
                                        {selectedLine?.inscor__Quantity_Available__c ?? ''}
                                    </td>
                                    <td
                                        className='pl-1 sm:pl-4 flex-[1.2_1_0%] min-w-0 py-0.5 sm:py-0 text-[10px] sm:text-sm break-words truncate'
                                        title={selectedLine?.originalLocationName || ''}
                                    >
                                        {selectedLine?.originalLocationName || ''}
                                    </td>
                                    {selectedLine && (
                                        <td className='pl-1 sm:pl-4 flex-[0.3_1_0%] min-w-0 py-0.5 sm:py-0'>
                                            <button
                                                type='button'
                                                className='w-4 h-4 text-[8px] text-center text-white bg-red-500 rounded-full sm:w-6 sm:h-6 hover:bg-red-400 sm:text-xs'
                                                onClick={(buttonClickEvent) => {
                                                    buttonClickEvent.preventDefault();
                                                    buttonClickEvent.stopPropagation();
                                                    handleRemoveSelectedLine(selectedLine.Id);
                                                }}
                                            >
                                                X
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
